import { faArrowsTurnToDots, faBoxOpen, faComment, faFlask, faMagnifyingGlass, faNoteSticky, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { use, useContext, useEffect, useState } from 'react';
import { Accordion, Spinner } from 'react-bootstrap';

import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { UserPreferencesContext } from '../../../Contexts/UserPreferencesContext';
import { QualityColumnType } from '../../../Types/LotDetailQuality';
import { Section } from '../../../Types/Section';
import GradingCommentsSection from '../LotDetailsComponents/GradingCommentsSection';
import LabResultsSection, { LabResultsColumnType } from '../LotDetailsComponents/LabResultsSection';
import PackagesSection, { PackageColumnType } from '../LotDetailsComponents/PackagesSection/PackagesSection';
import QIRSection_QIRMS, { QirColumnType } from '../LotDetailsComponents/QIRSection_QIRMS';
import QualitiesSection from '../LotDetailsComponents/QualitiesSection';
import RelatedLotsSection from '../LotDetailsComponents/RelatedLotsSection';
import LotDetailSummary from '../LotDetailsComponents/Summary';
import WarningsSection from '../LotDetailsComponents/WarningsSection';
import { LotDetailSection } from '../LotDetailSection';
import { SectionNavigation } from '../SectionNavigation';
import { useTranslation } from 'react-i18next';

function LotDetails_SCP_SBA() {
    const {
        lotDetail,
        lotDetail: {
            summary: {
                businessUnitCode,
            },
            labResults,
            packages
        }
    } = useContext(LotDetailContext);

    const { t } = useTranslation();
    const { userPreferences, loading: userPreferencesLoading, setActiveSections } = use(UserPreferencesContext);
    const [loading, setLoading] = useState(false);

    const qualityColumns: QualityColumnType[] = [
        "Name",
        "SampleUsages",
        "Units",
        "Min",
        "Max",
        "LBMLab"
    ];

    if (businessUnitCode === "SCP_SAP") {
        qualityColumns.push("SalesMin", "SalesMax");
    } else if (businessUnitCode === "SCP_SBP") {
        qualityColumns.push("LRL", "URL");
    }
    qualityColumns.push("displayOrder");

    const packageColumns: PackageColumnType[] = [
        "Grade",
        "PackageNumber",
        "PackageDate",
        "NetWeight",
        "Type",
        ...(businessUnitCode === "SCP_SBP" ? ["Rfid"] as PackageColumnType[] : []),
        "Notes",
        "QIRs"
    ];

    const labResultColumns: LabResultsColumnType[] = [
        "SampleTime",
        "Lot",
        "Grade",
        "Quality",
        "Analysis",
        "Component",
        "SamplingPoint",
        "SMSampleId",
        "Value",
        "Units"
    ];

    const qirColumns: QirColumnType[] = [
        "IncidentId",
        "LotNumber",
        "Category",
        "Statement",
        "Subcategory",
        "GradingAction",
        "GradingActionNotes",
        "LotSplitInfo",
        "Status"
    ];

    const sections: Section[] = [
        { name: 'warnings', title: 'LotDetails.Warnings.Title', icon: faTriangleExclamation, component: <WarningsSection /> },
        { name: 'QIRMSqirs', title: 'LotDetails.QIRs.Title', icon: faNoteSticky, component: <QIRSection_QIRMS showCreateQIRButton={true} customColumns={qirColumns} /> },
        { name: 'relatedLots', title: 'LotDetails.RelatedLots.Title', icon: faArrowsTurnToDots, component: <RelatedLotsSection /> },
        { name: 'qualities', title: 'LotDetails.Qualities.Title', icon: faMagnifyingGlass, component: <QualitiesSection customQualityColumns={qualityColumns} /> },
        { name: 'labresults', title: 'LotDetails.LabResults.Title', titleSuffix: `${labResults.length}`, icon: faFlask, component: <LabResultsSection customColumns={labResultColumns} /> },
        { name: 'packages', title: 'LotDetails.Packages.Title', titleSuffix: `${packages.length}`, icon: faBoxOpen, component: <PackagesSection packagesCanBeEdited={true} customColumns={packageColumns} /> },
        { name: 'gradingcomments', title: 'LotDetails.GradingComments.Title', icon: faComment, component: <GradingCommentsSection /> },
    ];

    const [defaultActiveSections, setDefaultActiveSections] = useState(sections.map(section => section.name));
    useEffect(() => {
        if (userPreferences?.lotDetail?.activeSections) {
            setDefaultActiveSections(userPreferences?.lotDetail?.activeSections);
        }
    }, [userPreferences?.lotDetail?.activeSections]);

    if (userPreferencesLoading) {
        return (
            <div className="d-flex justify-content-center mb-3">
                <Spinner animation="border" />
            </div>
        );
    }

    return (lotDetail &&
        <>
            <LotDetailSummary setLoading={setLoading} />

            <SectionNavigation sections={sections} />

            <Accordion activeKey={defaultActiveSections} onSelect={(eventKey) => setActiveSections(eventKey as string[])} alwaysOpen className="mb-2">
                {sections.map(section =>
                    <LotDetailSection
                        key={section.name}
                        name={section.name}
                        icon={section.icon}
                        title={section.title}
                        titleSuffix={section.titleSuffix}
                        component={section.component} />)
                }
            </Accordion>
        </>
    );
}

export default LotDetails_SCP_SBA;